import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Divider,
    Chip,
    DialogContentText,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUser, faTrash, faCrown, faExclamationTriangle, faUserPlus, faKey } from '@fortawesome/free-solid-svg-icons';
import { User } from '../types';
import apiService from '../services/api';
import { CreateUserModal } from './CreateUserModal';
import { ResetPasswordModal } from './ResetPasswordModal';

interface UsersManagementModalProps {
    open: boolean;
    onClose: () => void;
    currentUserId: number;
}

export const UsersManagementModal: React.FC<UsersManagementModalProps> = ({
    open,
    onClose,
    currentUserId,
}) => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [deletingUsers, setDeletingUsers] = useState<number[]>([]);
    const [error, setError] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<{
        open: boolean;
        user: User | null;
    }>({ open: false, user: null });
    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
    const [resetPasswordModal, setResetPasswordModal] = useState<{
        open: boolean;
        user: User | null;
    }>({ open: false, user: null });

    const fetchUsers = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiService.getAllUsers();
            setUsers(response.data.users);
        } catch (err: any) {
            setError('Failed to load users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchUsers();
        }
    }, [open]);

    const handleDeleteUser = async (user: User) => {
        setDeletingUsers(prev => [...prev, user.userId]);
        setError('');
        
        try {
            await apiService.deleteUser(user.userId);
            setUsers(users.filter(u => u.userId !== user.userId));
            setDeleteConfirm({ open: false, user: null });
        } catch (err: any) {
            setError('Failed to delete user');
            console.error('Error deleting user:', err);
        } finally {
            setDeletingUsers(prev => prev.filter(id => id !== user.userId));
        }
    };

    const isAdmin = (userId: number) => {
        return [1, 2, 3].includes(userId);
    };

    const handleClose = () => {
        setUsers([]);
        setError('');
        setDeletingUsers([]);
        setDeleteConfirm({ open: false, user: null });
        setResetPasswordModal({ open: false, user: null });
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    },
                }}
            >
                            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon icon={faUsers} />
                        User Management
                    </Box>
                    <Button
                        onClick={() => setIsCreateUserModalOpen(true)}
                        startIcon={<FontAwesomeIcon icon={faUserPlus} />}
                        variant="contained"
                        size="small"
                        sx={{
                            bgcolor: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.dark',
                            },
                        }}
                    >
                        Create User
                    </Button>
                </Box>
            </DialogTitle>
                <DialogContent>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : users.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <FontAwesomeIcon 
                                icon={faUsers} 
                                style={{ fontSize: '2rem', opacity: 0.5, marginBottom: '1rem' }} 
                            />
                            <Typography color="text.secondary">
                                No users found.
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {users.map((user, index) => (
                                <React.Fragment key={user.userId}>
                                    <ListItem
                                        sx={{
                                            py: 1.5,
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                                borderRadius: 1,
                                            },
                                        }}
                                        secondaryAction={
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {isAdmin(user.userId) && (
                                                    <Chip
                                                        label="Admin"
                                                        size="small"
                                                        icon={<FontAwesomeIcon icon={faCrown} />}
                                                        sx={{
                                                            bgcolor: 'warning.main',
                                                            color: 'white',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                )}
                                                <Tooltip title="Reset password">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setResetPasswordModal({ open: true, user })}
                                                        sx={{
                                                            color: 'text.secondary',
                                                            '&:hover': {
                                                                color: 'primary.main',
                                                                bgcolor: 'primary.light',
                                                            },
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faKey} />
                                                    </IconButton>
                                                </Tooltip>
                                                {currentUserId !== user.userId && !isAdmin(user.userId) && (
                                                    <Tooltip title="Delete user">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setDeleteConfirm({ open: true, user })}
                                                            disabled={deletingUsers.includes(user.userId)}
                                                            sx={{
                                                                color: 'text.secondary',
                                                                '&:hover': {
                                                                    color: 'error.main',
                                                                    bgcolor: 'error.light',
                                                                },
                                                            }}
                                                        >
                                                            {deletingUsers.includes(user.userId) ? (
                                                                <CircularProgress size={20} />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        }
                                    >
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                                <FontAwesomeIcon icon={faUser} />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {user.name}
                                                    {currentUserId === user.userId && (
                                                        <Chip
                                                            label="You"
                                                            size="small"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: '0.75rem',
                                                                bgcolor: 'primary.main',
                                                                color: 'white',
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {user.email}
                                                    </Typography>
                                                    {user.deletedAt && (
                                                        <Chip
                                                            label="Deleted"
                                                            size="small"
                                                            sx={{
                                                                bgcolor: 'error.main',
                                                                color: 'white',
                                                                fontSize: '0.75rem',
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            primaryTypographyProps={{
                                                variant: 'subtitle2',
                                                fontWeight: 500,
                                            }}
                                            secondaryTypographyProps={{
                                                variant: 'body2',
                                                color: 'text.secondary',
                                            }}
                                        />
                                    </ListItem>
                                    {index < users.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
                    <Button onClick={handleClose}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirm.open}
                onClose={() => setDeleteConfirm({ open: false, user: null })}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f57c00' }} />
                        Delete User
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the user <strong>{deleteConfirm.user?.name}</strong> ({deleteConfirm.user?.email})? 
                        This action cannot be undone and will permanently remove the user from the system.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
                    <Button 
                        onClick={() => setDeleteConfirm({ open: false, user: null })}
                        disabled={deletingUsers.includes(deleteConfirm.user?.userId || 0)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => deleteConfirm.user && handleDeleteUser(deleteConfirm.user)}
                        color="error"
                        variant="contained"
                        disabled={deletingUsers.includes(deleteConfirm.user?.userId || 0)}
                        startIcon={deletingUsers.includes(deleteConfirm.user?.userId || 0) ? <CircularProgress size={20} /> : <FontAwesomeIcon icon={faTrash} />}
                    >
                        {deletingUsers.includes(deleteConfirm.user?.userId || 0) ? 'Deleting...' : 'Delete User'}
                    </Button>
                </DialogActions>
            </Dialog>

            <CreateUserModal
                open={isCreateUserModalOpen}
                onClose={() => setIsCreateUserModalOpen(false)}
                onUserCreated={fetchUsers}
            />

            <ResetPasswordModal
                open={resetPasswordModal.open}
                onClose={() => setResetPasswordModal({ open: false, user: null })}
                user={resetPasswordModal.user}
            />
        </>
    );
};
